import * as React from 'react';

import { RecommendationSkeleton } from '../../components/Recommendation';
import { RecommendationsList } from '../../components/RecommendationsList';
import { RedesignedPaginationError } from '../../components/RedesignedPaginationError';
import { RecommendationSkeleton as RedesignedRecommendationSkeleton } from '../../components/RedesignedRecommendation';
import { RecommendationsList as RedesignedRecommendationsList } from '../../components/RedesignedRecommendationsList';
import { ELoadingStatus } from '../../types/loadingStatus';
import { MainPageExperiment } from '../MainPageExperiment';
import { RedesignedRecommendationsListMobile } from '../RedesignedRecommendationsListMobile';
import { RecommendationsListMobile, RecommendationsPaginationError } from './RecommendationsListMobile';
import type { useRecommendations } from './useRecommendations';

const MOBILE_SKELETON_COUNT = 20;

type TTrackRecommendationShow = (offerId: number | null, position: number, contextToken?: string) => void;

type TRecommendationsListMobileContainerProps = {
  recommendationsControls: ReturnType<typeof useRecommendations>;
  onTrack: TTrackRecommendationShow;
  isMainPage: boolean;
  infiniteScroll: boolean;
};

export const RecommendationsListMobileContainer: React.FC<TRecommendationsListMobileContainerProps> = ({
  recommendationsControls,
  onTrack,
  isMainPage,
  infiniteScroll,
}) => {
  const { recommendations, recommendationsStatus, showHeader, onRetryRecommendations } = recommendationsControls;

  const legacyMobileSkeleton = React.useMemo(
    () =>
      Array.from({ length: MOBILE_SKELETON_COUNT }, (_, index) => (
        <RecommendationSkeleton key={`recommendation-mobile-skeleton-${index}`} />
      )),
    [],
  );

  const redesignedMobileSkeleton = React.useMemo(
    () =>
      Array.from({ length: MOBILE_SKELETON_COUNT }, (_, index) => (
        <RedesignedRecommendationSkeleton key={`recommendation-mobile-skeleton-${index}`} />
      )),
    [],
  );

  if (recommendationsStatus === ELoadingStatus.Loading) {
    return (
      <MainPageExperiment
        isMainPage={isMainPage}
        legacy={<RecommendationsList withHeader={showHeader}>{legacyMobileSkeleton}</RecommendationsList>}
        redesigned={<RedesignedRecommendationsList>{redesignedMobileSkeleton}</RedesignedRecommendationsList>}
      />
    );
  }

  if (recommendationsStatus === ELoadingStatus.Failure) {
    return (
      <MainPageExperiment
        isMainPage={isMainPage}
        legacy={
          <RecommendationsList withHeader={showHeader}>
            <RecommendationsPaginationError onRetry={onRetryRecommendations} />
          </RecommendationsList>
        }
        redesigned={
          <RedesignedRecommendationsList>
            <RedesignedPaginationError onRetry={onRetryRecommendations} />
          </RedesignedRecommendationsList>
        }
      />
    );
  }

  if (!recommendations.length) {
    return null;
  }

  return (
    <MainPageExperiment
      isMainPage={isMainPage}
      legacy={
        <RecommendationsListMobile
          recommendationsControls={recommendationsControls}
          onTrack={onTrack}
          infiniteScroll={infiniteScroll}
        />
      }
      redesigned={
        <RedesignedRecommendationsListMobile recommendationsControls={recommendationsControls} onTrack={onTrack} />
      }
    />
  );
};
